import { excludeRoutes } from 'constants/locations';
import {
  ISOLocale,
  ISOLocalesEnum,
  LocaleEnum,
  ShortLocaleEnum,
  fullLocales,
  languages,
  ShopifyLocaleCode,
  BCP47Language
} from 'schema/locale';
import { QAStoreOptions, StoreEnum, StoreOptions } from 'schema/storeCode';
import type { PageTranslation } from 'schema/studio/studio';
import { hasQa } from 'shared/hooks/useHasQa';

const {
  deEU,
  enDev,
  enQa,
  enAud,
  enNzd,
  enUS,
  apacUS,
  enUK,
  enUkd,
  enEU,
  enEud,
  nlEU,
  seEU,
  esEU,
  frEU
} = LocaleEnum;
const { de, en, nl, se, es, fr, apac } = ShortLocaleEnum;

// terminology:
// short locale = language (ie "en", "de")
// store = region (ie "us", "eu")
// locale = full locale = ISO language = [language]-[region] (ie "en-us", "de-eu")

/**
 * Get short locale ("en", "de", etc) based on full locale ("en-us", "de-eu", etc)
 */
export const getShortLocale = (locale: LocaleEnum | string) => {
  if (locale === deEU || locale?.startsWith('de')) return de;
  if (locale === nlEU || locale?.startsWith('nl')) return nl;
  if (locale === seEU || locale?.startsWith('se') || locale?.startsWith('sv')) return se; // Swedish is represented by "sv" and "se"
  if (locale === esEU || locale?.startsWith('es')) return es;
  if (locale === frEU || locale?.startsWith('fr')) return fr;
  if (locale === apacUS || locale?.startsWith('apac')) return apac;
  return en;
};

/**
 * Get shopify language ("en", "de", etc) based on full locale ("en-us", "de-eu", etc)
 */
export const getShopifyLanguage = (locale: LocaleEnum | string) => {
  if (locale === deEU || locale?.startsWith('de')) return de;
  if (locale === nlEU || locale?.startsWith('nl')) return nl;
  if (locale === seEU || locale?.startsWith('se') || locale?.startsWith('sv')) return 'sv';
  if (locale === esEU || locale?.startsWith('es')) return es;
  if (locale === frEU || locale?.startsWith('fr')) return fr;
  return en;
};

/**
 * Safely fetch full locale ("en-us", "de-eu", etc) that is in LocaleEnum specifically
 */
export const getSafeLocale = (locale: string | undefined | null = '') => {
  if (locale && (languages as string[]).includes(locale)) {
    return locale as LocaleEnum;
  }
  return enUS;
};
/**
 * Safely fetch full locale ("en-us", "de-eu", etc) that is in LocaleEnum specifically
 * but convert dev and qa store to use the proper locale store
 */
export const getSafeLocaleForSanity = (locale: string | undefined | null = '') => {
  if (locale && (languages as string[]).includes(locale)) {
    if (locale === enDev || locale === enQa || locale === enAud || locale === enNzd) return enUS;
    if (locale === enUkd) return enUK;
    if (locale === enEud) return enEU;
    return locale as LocaleEnum;
  }
  return enUS;
};

export const getShopifyLocale = (locale: LocaleEnum | string | null = enUS) => {
  return ShopifyLocaleCode[locale as keyof typeof ShopifyLocaleCode] || ShopifyLocaleCode[enUS];
};

/**
 * fetch ISO Language ("en-us", "de-eu", etc)
 * by short locale ("en", "de", etc) and store ("us", "eu", etc)
 * (serves same functionality as `getFullLocale`, but does
 * NOT accept full locale enum as the `shortLocale` argument)
 */
export const getISOLanguage = (
  shortLocale: ShortLocaleEnum | string | null = en,
  store: StoreEnum | string | null = StoreEnum.us
) => {
  const foundLocale = fullLocales.find(
    (item) => item.store === store && item.language === shortLocale
  );
  return foundLocale?.isoLanguage || ISOLocalesEnum['en-us'];
};

/**
 * fetch ISO Language ("en-us", "de-eu", etc)
 * by short locale ("en", "de", etc) and store ("us", "eu", etc)
 * (serves same functionality as `getISOLanguage`, but DOES
 * accept full locale enum as the `shortLocale` argument)
 */
export const getFullLocale = (
  shortLocale: ShortLocaleEnum | LocaleEnum | string | undefined = en,
  store: StoreEnum | string | undefined | null = StoreEnum.us
) => {
  // if shortLocale provided already contains both language and region
  // and can be found in the list, return it
  const isFullLocale = languages.some((item) => item === shortLocale);
  if (isFullLocale) return shortLocale as LocaleEnum;

  // Get full list of locales for specific store
  const storeLocales = fullLocales.filter((item) => item.store === store);

  // Return full locale with specific store and language or first of them
  const foundLocale =
    storeLocales.find((item) => item.language === shortLocale) || storeLocales[0] || fullLocales[0];

  return foundLocale.locale;
};

export const getISOLocale = <T extends LocaleEnum>(locale: T) =>
  ISOLocale[locale] || ISOLocale[enUS];

export const getBCP47Language = (locale: string | undefined | null) =>
  locale && locale in BCP47Language
    ? BCP47Language[locale as keyof typeof BCP47Language]
    : BCP47Language[enUS];

interface TranslationSlugProps {
  /**
   * Full route path, better is **router.asPath**
   */
  path: string;
  slug?: string;
  store?: StoreEnum | string;
  locale?: LocaleEnum | ShortLocaleEnum | string;
}

export const getTranslationSlug = ({ path, slug, store, locale }: TranslationSlugProps) => {
  const language = getFullLocale(locale, store);

  const isExclude = excludeRoutes.some((item) => path.startsWith(item));
  /**
   * For en-us locale or routes without localization (e.g. /members)
   * set empty language prefix
   */
  const localePrefix = language === enUS || isExclude ? '' : language;

  return `/${localePrefix}/${slug || path || ''}`.replace(/\/+/g, '/');
};

export const getPageTranslations = (slug: string): PageTranslation[] =>
  languages.map((locale) => ({
    slug: {
      _type: 'slug',
      current: `${slug}`
    },
    language: locale
  }));

export const getStoreOptions = () => {
  if (hasQa()) return QAStoreOptions;
  return StoreOptions;
};
